import {OnboardedCountry} from '@/types/Markets'
import {showNotification} from '@ReduxActions'
import type {Table} from '@tanstack/react-table'
import {DataTable} from 'pepsico-ds'
import {useCallback, useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {store} from '../../store'
import {getTableColumns} from './components/columns'
import {useOnboardedCountries} from './hooks/useOnboardedCountries'

export const MarketsTable = () => {
  const {formatMessage} = useIntl()
  const {isFetching, data, error} = useOnboardedCountries()

  // true only on the first load; re-fetches after onboarding a country keep the current rows
  const isInitialLoading = isFetching && !data

  useEffect(() => {
    if (error) {
      onLoadMarketsError()
    }
  }, [error])

  const columns = useMemo(() => getTableColumns(formatMessage), [formatMessage])

  const onTableReady = useCallback((table: Table<OnboardedCountry>) => {
    table.setSorting([DEFAULT_SORTING])
  }, [])

  return (
    <>
      {isInitialLoading && (
        <p className="text-md my-16 items-center text-center" data-testid="markets-loading">
          {formatMessage({id: 'MARKETS.LOADING_TABLE_LABEL'})}
        </p>
      )}
      {!isInitialLoading && error && (
        <p className="text-md my-16 items-center text-center" data-testid="markets-error">
          {formatMessage({id: 'MARKETS.ERROR_TABLE_LABEL'})}
        </p>
      )}
      {!isInitialLoading && !error && (
        <div data-testid="markets-content">
          <DataTable
            columns={columns}
            data={data ?? []}
            isLoading={isFetching}
            enableSorting
            showPagination
            showColumnFilters={false}
            showTableHeader={false}
            noDataTitleText={formatMessage({id: 'MARKETS.EMPTY_TABLE_LABEL'})}
            noDataDescriptionText=""
            onTableReady={onTableReady}
            data-cy="markets-content"
          />
        </div>
      )}
    </>
  )
}

const DEFAULT_SORTING = {id: 'name', desc: false}

const onLoadMarketsError = () => {
  store.dispatch(
    showNotification({
      show: true,
      type: 'error',
      title: 'Something went wrong!',
      message: 'Please try again later.',
    })
  )
}
